// src/reducers/secondaryAccountSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import {
  updateSecondaryAccount,
  deleteSecondary,
} from "../actions/authenActions";

const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000";

// fetch all secondary accounts
export const fetchSecondaryAccounts = createAsyncThunk(
  "secondaryAccount/fetchAll",
  async ({ license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/licensing/api/secondary_account/list/`,
        {
          license_key,
        },
      );
      return res.data?.accounts || [];
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// add secondary account with role
export const addSecondaryAccount = createAsyncThunk(
  "secondaryAccount/add",
  async (
    { email, fullname, role, license_key },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const res = await axios.post(
        `${baseURL}/licensing/api/secondary_account/add/`,
        {
          email,
          fullname,
          role,
          license_key,
        },
      );
      dispatch(updateSecondaryAccount({ email, fullname, role }));
      return res.data || {};
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// update secondary account role
export const updateSecondaryAccountRole = createAsyncThunk(
  "secondaryAccount/updateRole",
  async ({ email, role, license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/licensing/api/secondary_account/update_role/`,
        {
          email,
          role,
          license_key,
        },
      );
      return { email, role, ...res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// delete secondary account
export const deleteSecondaryAccount = createAsyncThunk(
  "secondaryAccount/delete",
  async ({ email, license_key }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/licensing/api/secondary_account/delete/`,
        {
          email,
          license_key,
        },
      );
      dispatch(deleteSecondary());
      return { email, ...res.data };
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

// audit secondary account
export const auditSecondaryAccount = createAsyncThunk(
  "secondaryAccount/audit",
  async ({ license_key }, { rejectWithValue }) => {
    try {
      const res = await axios.post(
        `${baseURL}/licensing/api/secondary_account/audit/`,
        {
          license_key,
        },
      );
      return res.data || {};
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  },
);

const secondaryAccountSlice = createSlice({
  name: "secondaryAccount",
  initialState: {
    accounts: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all accounts
      .addCase(fetchSecondaryAccounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSecondaryAccounts.fulfilled, (state, action) => {
        state.loading = false;
        state.accounts = action.payload;
      })
      .addCase(fetchSecondaryAccounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Add account
      .addCase(addSecondaryAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addSecondaryAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // Add the new account to the list if not already present
        if (
          !state.accounts.find((acc) => acc.email === action.meta.arg.email)
        ) {
          state.accounts.push({
            email: action.meta.arg.email,
            fullname: action.meta.arg.fullname,
            role: action.meta.arg.role,
          });
        }
      })
      .addCase(addSecondaryAccount.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.status ||
          action.payload?.message ||
          "Add API failed.";
      })
      // Update role
      .addCase(updateSecondaryAccountRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateSecondaryAccountRole.fulfilled, (state, action) => {
        state.loading = false;
        const account = state.accounts.find(
          (acc) => acc.email === action.payload.email,
        );
        if (account) {
          account.role = action.payload.role;
        }
      })
      .addCase(updateSecondaryAccountRole.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.status ||
          action.payload?.message ||
          "Update role failed.";
      })
      // Delete account
      .addCase(deleteSecondaryAccount.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteSecondaryAccount.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.accounts = state.accounts.filter(
          (acc) => acc.email !== action.payload.email,
        );
      })
      .addCase(deleteSecondaryAccount.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.status ||
          action.payload?.message ||
          "Delete API failed.";
      })
      // Audit
      .addCase(auditSecondaryAccount.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(auditSecondaryAccount.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearError } = secondaryAccountSlice.actions;
export default secondaryAccountSlice.reducer;
